import React, { useEffect, useMemo, useState } from "react";
import TableGeneric from "../../../../shared/components/generic/table/table-generic";
import AdminSection from "../../components/admin-section/admin-section";
import { latePaymentPenaltyColumnsConfig } from "../../config/penalidade-inadimplencia/late-payment-penalty-columns-config";
import { declineNetSalesColumnsConfig } from "../../config/penalidade-inadimplencia/decline-net-sales-columns-config";
import { showConfirmAlert } from "../../../../shared/components/generic/alert/confirm-alert/confirm-alert";
import { useAdminFormModal } from "../../hooks/useAdminFormModal";
import FormModal from "../../../../shared/components/generic/form/form-modal/form-modal";
import { declineNetSalesFormConfig } from "../../config/penalidade-inadimplencia/decline-net-sales-form-config";
import { latePaymentPenaltyFormConfig } from "../../config/penalidade-inadimplencia/late-payment-penalty-form-config";
import { confirmSaveChanges, showNoRecordsFoundAlert } from "../../utils/admin-utils";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import Pagination from "../../../../shared/components/generic/pagination/pagination";
import { useLatePaymentPenalties } from "../../hooks/penalidade-inadimplencia/late-payment/useLatePaymentPenalties";
import { useLatePaymentPenalty } from "../../hooks/penalidade-inadimplencia/late-payment/useLatePaymentPenalty";
import { useLatePaymentPenaltyUpdate } from "../../hooks/penalidade-inadimplencia/late-payment/useLatePaymentPenaltyUpdate";
import { useLatePaymentPenaltyDelete } from "../../hooks/penalidade-inadimplencia/late-payment/useLatePaymentPenaltyDelete";
import { useLatePaymentPenaltyById } from "../../hooks/penalidade-inadimplencia/late-payment/useLatePaymentPenaltyById";
import { useDeclineNetSales } from "../../hooks/penalidade-inadimplencia/decline-net-sales/useDeclineNetSales";
import { useDeclineNetSalesNew } from "../../hooks/penalidade-inadimplencia/decline-net-sales/useDeclineNetSalesNew";
import { useDeclineNetSalesUpdate } from "../../hooks/penalidade-inadimplencia/decline-net-sales/useDeclineNetSalesUpdate";
import { useDeclineNetSalesDelete } from "../../hooks/penalidade-inadimplencia/decline-net-sales/useDeclineNetSalesDelete";
import { useDeclineNetSalesById } from "../../hooks/penalidade-inadimplencia/decline-net-sales/useDeclineNetSalesById";
import {
	hasValidationErrors,
	removeClearedValidationErrors
} from "../../../utils/validation-utils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import MonetaryUtils from "../../../../shared/utils/MonetaryUtils";

const isConfirmAccepted = result => {
	return result?.isConfirmed === true || result?.value === true;
};

const PenalidadePorInadimplencia = () => {
	const paymentPenaltyModal = useAdminFormModal();
	const declineNetSalesModal = useAdminFormModal();

	const [penaltyActionLoadingMessage, setPenaltyActionLoadingMessage] = useState("");
	const [paymentPenaltyValidationErrors, setPaymentPenaltyValidationErrors] = useState({});
	const [clearedPaymentPenaltyValidationErrors, setClearedPaymentPenaltyValidationErrors] = useState({});
	const [declineNetSalesValidationErrors, setDeclineNetSalesValidationErrors] = useState({});
	const [clearedDeclineNetSalesValidationErrors, setClearedDeclineNetSalesValidationErrors] = useState({});

	const [latePaymentPenaltyPagination, setLatePaymentPenaltyPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});

	const [declineNetSalesPagination, setDeclineNetSalesPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});

	const {
		latePaymentPenalties,
		loading: latePaymentPenaltiesLoading,
		refetch: fetchLatePaymentPenalties
	} = useLatePaymentPenalties();

	const {
		loading: latePaymentPenaltyByIdLoading,
		fetchLatePaymentPenaltyById
	} = useLatePaymentPenaltyById();

	const {
		loading: createLatePaymentPenaltyLoading,
		validateLatePaymentPenalty,
		createLatePaymentPenalty
	} = useLatePaymentPenalty();

	const {
		loading: updateLatePaymentPenaltyLoading,
		updateLatePaymentPenalty
	} = useLatePaymentPenaltyUpdate();

	const {
		loading: deleteLatePaymentPenaltyLoading,
		deleteLatePaymentPenalty
	} = useLatePaymentPenaltyDelete();

	const {
		declineNetSales,
		loading: declineNetSalesLoading,
		refetch: fetchDeclineNetSales
	} = useDeclineNetSales();

	const {
		loading: declineNetSalesByIdLoading,
		fetchDeclineNetSalesById
	} = useDeclineNetSalesById();

	const {
		loading: createDeclineNetSalesLoading,
		validateDeclineNetSales,
		createDeclineNetSales
	} = useDeclineNetSalesNew();

	const {
		loading: updateDeclineNetSalesLoading,
		updateDeclineNetSales
	} = useDeclineNetSalesUpdate();

	const {
		loading: deleteDeclineNetSalesLoading,
		deleteDeclineNetSales
	} = useDeclineNetSalesDelete();

	const isLoadingLatePaymentPenalty =
		latePaymentPenaltiesLoading ||
		latePaymentPenaltyByIdLoading;

	const isLoadingDeclineNetSales =
		declineNetSalesLoading ||
		declineNetSalesByIdLoading;

	const isLoadingPenalties =
		isLoadingLatePaymentPenalty ||
		isLoadingDeclineNetSales;

	const isSavingLatePaymentPenalty =
		createLatePaymentPenaltyLoading ||
		updateLatePaymentPenaltyLoading;

	const isDeletingLatePaymentPenalty = deleteLatePaymentPenaltyLoading;

	const isSavingDeclineNetSales =
		createDeclineNetSalesLoading ||
		updateDeclineNetSalesLoading;

	const isDeletingDeclineNetSales = deleteDeclineNetSalesLoading;

	const isPenaltyModalOpen =
		paymentPenaltyModal.isOpen ||
		declineNetSalesModal.isOpen;

	const visiblePaymentPenaltyValidationErrors = removeClearedValidationErrors({
		errors: paymentPenaltyValidationErrors,
		clearedErrors: clearedPaymentPenaltyValidationErrors
	});

	const visibleDeclineNetSalesValidationErrors = removeClearedValidationErrors({
		errors: declineNetSalesValidationErrors,
		clearedErrors: clearedDeclineNetSalesValidationErrors
	});

	const loadLatePaymentPenalties = async (
		pagination = latePaymentPenaltyPagination,
		{ showEmptyAlert = true } = {}
	) => {
		const result = await fetchLatePaymentPenalties({
			page: pagination.page,
			size: pagination.size
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar as penalidades por inadimplência.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		setLatePaymentPenaltyPagination(result.pagination);

		if (result.empty && showEmptyAlert) {
			await showNoRecordsFoundAlert();
		}

		return result;
	};

	const loadDeclineNetSales = async (
		pagination = declineNetSalesPagination,
		{ showEmptyAlert = true } = {}
	) => {
		const result = await fetchDeclineNetSales({
			page: pagination.page,
			size: pagination.size
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar as penalidades por queda em Net Sales.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		setDeclineNetSalesPagination(result.pagination);

		if (result.empty && showEmptyAlert) {
			await showNoRecordsFoundAlert();
		}

		return result;
	};

	const loadPenalties = async () => {
		const [
			latePaymentResult,
			declineNetSalesResult
		] = await Promise.all([
			loadLatePaymentPenalties(
				{
					page: 0,
					size: latePaymentPenaltyPagination.size
				},
				{
					showEmptyAlert: false
				}
			),
			loadDeclineNetSales(
				{
					page: 0,
					size: declineNetSalesPagination.size
				},
				{
					showEmptyAlert: false
				}
			)
		]);

		const hasOnlyEmptyResults =
			latePaymentResult?.ok &&
			declineNetSalesResult?.ok &&
			latePaymentResult.empty &&
			declineNetSalesResult.empty;

		if (hasOnlyEmptyResults) {
			await showNoRecordsFoundAlert();
		}
	};

	useEffect(() => {
		loadPenalties();
	}, []);

	const handlePageChangeLatePaymentPenalty = ({ selected }) => {
		loadLatePaymentPenalties({
			...latePaymentPenaltyPagination,
			page: selected
		});
	};

	const handlePageChangeDeclineNetSales = ({ selected }) => {
		loadDeclineNetSales({
			...declineNetSalesPagination,
			page: selected
		});
	};

	const handleOpenPaymentPenaltyCreate = () => {
		setPaymentPenaltyValidationErrors({});
		setClearedPaymentPenaltyValidationErrors({});
		paymentPenaltyModal.openCreate();
	};

	const handleOpenDeclineNetSalesCreate = () => {
		setDeclineNetSalesValidationErrors({});
		setClearedDeclineNetSalesValidationErrors({});
		declineNetSalesModal.openCreate();
	};

	const handleEditPaymentPenalty = async row => {
		setPaymentPenaltyValidationErrors({});
		setClearedPaymentPenaltyValidationErrors({});

		const result = await fetchLatePaymentPenaltyById(row.id);

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os dados da penalidade por inadimplência.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		paymentPenaltyModal.openEdit(result.latePaymentPenalty);
	};

	const handleEditNetSalesPenalty = async row => {
		setDeclineNetSalesValidationErrors({});
		setClearedDeclineNetSalesValidationErrors({});

		const result = await fetchDeclineNetSalesById(row.id);

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os dados da penalidade por queda em Net Sales.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		declineNetSalesModal.openEdit(result.declineNetSales);
	};

	const handleClosePaymentPenaltyModal = () => {
		setPaymentPenaltyValidationErrors({});
		setClearedPaymentPenaltyValidationErrors({});
		paymentPenaltyModal.close();
	};

	const handleCloseDeclineNetSalesModal = () => {
		setDeclineNetSalesValidationErrors({});
		setClearedDeclineNetSalesValidationErrors({});
		declineNetSalesModal.close();
	};

	const handlePaymentPenaltyFieldChange = ({ name }) => {
		setClearedPaymentPenaltyValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleDeclineNetSalesFieldChange = ({ name }) => {
		setClearedDeclineNetSalesValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleSubmitPaymentPenalty = async values => {
		const selectedItem = paymentPenaltyModal.selectedItem;
		const isEditing = paymentPenaltyModal.isEditing;

		const validationErrors = validateLatePaymentPenalty(values);

		if (hasValidationErrors(validationErrors)) {
			setClearedPaymentPenaltyValidationErrors({});
			setPaymentPenaltyValidationErrors(validationErrors);

			await showCommonAlert({
				title: "Atenção",
				html: Object.values(validationErrors).join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setPaymentPenaltyValidationErrors({});
		setClearedPaymentPenaltyValidationErrors({});
		paymentPenaltyModal.close();

		const confirmResult = await confirmSaveChanges({
			description: (
				<>
					Deseja salvar as alterações?
					<br />
					<strong>Atenção: Esta alteração afetará os contratos existentes e um novo aditivo precisará ser feito.</strong>
				</>
			)
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let saveResult;

		setPenaltyActionLoadingMessage("Salvando penalidade por inadimplência...");

		try {
			saveResult = isEditing
				? await updateLatePaymentPenalty(selectedItem.id, values)
				: await createLatePaymentPenalty(values);

			if (saveResult.ok) {
				await loadLatePaymentPenalties({
					page: isEditing ? latePaymentPenaltyPagination.page : 0,
					size: latePaymentPenaltyPagination.size
				});
			}
		} finally {
			setPenaltyActionLoadingMessage("");
		}

		if (!saveResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: saveResult.error,
				fallbackMessage: "Não foi possível salvar a penalidade por inadimplência.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		await showCommonAlert({
			title: "Sucesso",
			message: isEditing
				? "Penalidade por inadimplência atualizada com sucesso."
				: "Penalidade por inadimplência incluída com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleSubmitDeclineNetSalesPenalty = async values => {
		const selectedItem = declineNetSalesModal.selectedItem;
		const isEditing = declineNetSalesModal.isEditing;

		const validationErrors = validateDeclineNetSales(values);

		if (hasValidationErrors(validationErrors)) {
			setClearedDeclineNetSalesValidationErrors({});
			setDeclineNetSalesValidationErrors(validationErrors);

			await showCommonAlert({
				title: "Atenção",
				html: Object.values(validationErrors).join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setDeclineNetSalesValidationErrors({});
		setClearedDeclineNetSalesValidationErrors({});
		declineNetSalesModal.close();

		const confirmResult = await confirmSaveChanges({
			description: (
				<>
					Deseja salvar as alterações?
					<br />
					<strong>Atenção: Esta alteração afetará os contratos existentes e um novo aditivo precisará ser feito.</strong>
				</>
			)
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let saveResult;

		setPenaltyActionLoadingMessage("Salvando penalidade por queda em Net Sales...");

		try {
			saveResult = isEditing
				? await updateDeclineNetSales(selectedItem.id, values)
				: await createDeclineNetSales(values);

			if (saveResult.ok) {
				await loadDeclineNetSales({
					page: isEditing ? declineNetSalesPagination.page : 0,
					size: declineNetSalesPagination.size
				});
			}
		} finally {
			setPenaltyActionLoadingMessage("");
		}

		if (!saveResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: saveResult.error,
				fallbackMessage: "Não foi possível salvar a penalidade por queda em Net Sales.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		await showCommonAlert({
			title: "Sucesso",
			message: isEditing
				? "Penalidade por queda em Net Sales atualizada com sucesso."
				: "Penalidade por queda em Net Sales incluída com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleDeletePaymentPenalty = row => {
		showConfirmAlert({
			title: "Exclusão",
			description: (
				<>
					Tem certeza que deseja excluir a penalidade para inadimplências maiores que {MonetaryUtils.formatPercentage(row.delinquencyGreaterThan)} e penalidade de {MonetaryUtils.formatPercentage(row.penalty)}?
					<br />
					<strong>Atenção: Esta alteração afetará os contratos existentes e um novo aditivo precisará ser feito.</strong>
				</>
			),
			confirmLabel: "Sim, excluir",
			cancelLabel: "Cancelar"
		}).then(async result => {
			if (!isConfirmAccepted(result)) {
				return;
			}

			let deleteResult;
			let reloadResult;

			setPenaltyActionLoadingMessage("Excluindo penalidade por inadimplência...");

			try {
				deleteResult = await deleteLatePaymentPenalty(row.id);

				if (deleteResult.ok) {
					reloadResult = await loadLatePaymentPenalties(
						{
							page: latePaymentPenaltyPagination.page,
							size: latePaymentPenaltyPagination.size
						},
						{
							showEmptyAlert: false
						}
					);
				}
			} finally {
				setPenaltyActionLoadingMessage("");
			}

			if (!deleteResult.ok) {
				await showCommonAlert({
					title: "Erro",
					message: deleteResult.error,
					fallbackMessage: "Não foi possível excluir a penalidade por inadimplência.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			await showCommonAlert({
				title: "Sucesso",
				message: "Penalidade por inadimplência excluída com sucesso.",
				icon: "success",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			if (reloadResult?.empty) {
				await showNoRecordsFoundAlert();
			}
		});
	};

	const handleDeleteNetSalesPenalty = row => {
		showConfirmAlert({
			title: "Exclusão",
			description: (
				<>
					Tem certeza que deseja excluir a penalidade para quedas em Net Sales acima {MonetaryUtils.formatPercentage(row.declineGreaterThan)} e penalidade de {MonetaryUtils.formatPercentage(row.penalty)}?
					<br />
					<strong>Atenção: Esta alteração afetará os contratos existentes e um novo aditivo precisará ser feito.</strong>
				</>
			),
			confirmLabel: "Sim, excluir",
			cancelLabel: "Cancelar"
		}).then(async result => {
			if (!isConfirmAccepted(result)) {
				return;
			}

			let deleteResult;
			let reloadResult;

			setPenaltyActionLoadingMessage("Excluindo penalidade por queda em Net Sales...");

			try {
				deleteResult = await deleteDeclineNetSales(row.id);

				if (deleteResult.ok) {
					reloadResult = await loadDeclineNetSales(
						{
							page: declineNetSalesPagination.page,
							size: declineNetSalesPagination.size
						},
						{
							showEmptyAlert: false
						}
					);
				}
			} finally {
				setPenaltyActionLoadingMessage("");
			}

			if (!deleteResult.ok) {
				await showCommonAlert({
					title: "Erro",
					message: deleteResult.error,
					fallbackMessage: "Não foi possível excluir a penalidade por queda em Net Sales.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			await showCommonAlert({
				title: "Sucesso",
				message: "Penalidade por queda em Net Sales excluída com sucesso.",
				icon: "success",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			if (reloadResult?.empty) {
				await showNoRecordsFoundAlert();
			}
		});
	};

	const penaltyColumns = useMemo(() => {
		return latePaymentPenaltyColumnsConfig({
			onEdit: handleEditPaymentPenalty,
			onDelete: handleDeletePaymentPenalty
		});
	}, []);

	const declineNetSalesColumns = useMemo(() => {
		return declineNetSalesColumnsConfig({
			onEdit: handleEditNetSalesPenalty,
			onDelete: handleDeleteNetSalesPenalty
		});
	}, []);

	const latePaymentPenaltyFormFields = useMemo(() => {
		return latePaymentPenaltyFormConfig({
			paymentPenalty: paymentPenaltyModal.selectedItem
		});
	}, [paymentPenaltyModal.selectedItem]);

	const declineNetSalesFormFields = useMemo(() => {
		return declineNetSalesFormConfig({
			declineNetSales: declineNetSalesModal.selectedItem
		});
	}, [declineNetSalesModal.selectedItem]);

	return (
		<div className="admin-div late-payment-penalty">
			<PageLoading
				disabled={isPenaltyModalOpen}
				actionMessage={penaltyActionLoadingMessage}
				states={[
					{
						loading: isLoadingPenalties,
						message: "Carregando penalidades..."
					},
					{
						loading: isDeletingLatePaymentPenalty,
						message: "Excluindo penalidade por inadimplência..."
					},
					{
						loading: isDeletingDeclineNetSales,
						message: "Excluindo penalidade por queda em Net Sales..."
					},
					{
						loading: isSavingLatePaymentPenalty,
						message: "Salvando penalidade por inadimplência..."
					},
					{
						loading: isSavingDeclineNetSales,
						message: "Salvando penalidade por queda em Net Sales..."
					}
				]}
			/>
			<AdminSection
				title="Penalidade por inadimplência"
				actionLabel="Incluir penalidade"
				actionStartIcon="plusCircle"
				onAction={handleOpenPaymentPenaltyCreate}
			>
				<TableGeneric
					columns={penaltyColumns}
					data={latePaymentPenalties}
					rowKey="id"
				/>

				{latePaymentPenaltyPagination.totalPages > 1 && (
					<Pagination
						pageCount={latePaymentPenaltyPagination.totalPages}
						currentPage={latePaymentPenaltyPagination.page}
						onPageChange={handlePageChangeLatePaymentPenalty}
					/>
				)}
			</AdminSection>

			<AdminSection
				title="Penalidade por queda em Net Sales (Grupo)"
				actionLabel="Incluir penalidade"
				actionStartIcon="plusCircle"
				onAction={handleOpenDeclineNetSalesCreate}
			>
				<TableGeneric
					columns={declineNetSalesColumns}
					data={declineNetSales}
					rowKey="id"
				/>

				{declineNetSalesPagination.totalPages > 1 && (
					<Pagination
						pageCount={declineNetSalesPagination.totalPages}
						currentPage={declineNetSalesPagination.page}
						onPageChange={handlePageChangeDeclineNetSales}
					/>
				)}
			</AdminSection>

			<FormModal
				open={paymentPenaltyModal.isOpen}
				title={paymentPenaltyModal.isEditing ? "Editar penalidade por inadimplência" : "Incluir penalidade por inadimplência"}
				formId="late-payment-penalty-form"
				formFields={latePaymentPenaltyFormFields}
				errors={visiblePaymentPenaltyValidationErrors}
				onClose={handleClosePaymentPenaltyModal}
				onSubmit={handleSubmitPaymentPenalty}
				onFieldChange={handlePaymentPenaltyFieldChange}
				submitLabel="Salvar"
				cancelLabel="Cancelar"
				submitDisabled={
					isSavingLatePaymentPenalty ||
					isDeletingLatePaymentPenalty ||
					isLoadingPenalties ||
					Boolean(penaltyActionLoadingMessage)
				}
			/>

			<FormModal
				open={declineNetSalesModal.isOpen}
				title={declineNetSalesModal.isEditing ? "Editar penalidade por queda em Net Sales" : "Incluir penalidade por queda em Net Sales"}
				formId="decline-net-sales-penalty-form"
				formFields={declineNetSalesFormFields}
				errors={visibleDeclineNetSalesValidationErrors}
				onClose={handleCloseDeclineNetSalesModal}
				onSubmit={handleSubmitDeclineNetSalesPenalty}
				onFieldChange={handleDeclineNetSalesFieldChange}
				submitLabel="Salvar"
				cancelLabel="Cancelar"
				submitDisabled={
					isSavingDeclineNetSales ||
					isDeletingDeclineNetSales ||
					isLoadingPenalties ||
					Boolean(penaltyActionLoadingMessage)
				}
			/>
		</div>
	);
};

export default PenalidadePorInadimplencia;